import type {
  Account,
  Approval,
  Beneficiary,
  Card,
  Iban,
  Payment,
  PaymentAttempt,
  SmeCompanyView,
  Transaction,
  User,
} from '@shared/contracts/entities';
import type { MockScenario } from '@shared/contracts/mockApi';
import type { SmeFeatureFlags } from '@shared/contracts/featureFlags';
import type { LayoutView } from '@shared/contracts/customisation';
import {
  buildAccounts,
  buildApprovals,
  buildBeneficiaries,
  buildCards,
  buildCompanies,
  buildFeatureFlags,
  buildIbans,
  buildNotifications,
  buildPaymentAttempts,
  buildPayments,
  buildAuditHistory,
  buildTransactions,
  buildUsers,
  PROGRAMME,
  TENANT,
  type Notification,
} from '@/fixtures';
import { resetAudit, seedAudit } from '@/services/audit';
import { resetIdempotency } from '@/services/idempotency';
import { resetClock } from '@/services/clock';
import { clearAllDrafts } from '@/services/recoveryDraft';

/**
 * In-memory mock database.
 *
 * This is the source of truth for the running application. Browser storage is
 * never authoritative (12_CUSTOMISABLE_INTERFACE_ARCHITECTURE_V5.md); it only
 * ever holds an offline recovery draft.
 *
 * `resetDatabase()` restores every fixture deterministically, satisfying the
 * reset contract in 09_DEMO_FIXTURE_AND_DATA_SCENARIOS.md.
 */

export type MockDatabase = {
  tenant: typeof TENANT;
  programme: typeof PROGRAMME;
  companies: SmeCompanyView[];
  users: User[];
  accounts: Account[];
  ibans: Iban[];
  transactions: Transaction[];
  beneficiaries: Beneficiary[];
  payments: Payment[];
  paymentAttempts: PaymentAttempt[];
  approvals: Approval[];
  cards: Card[];
  notifications: Notification[];
  featureFlags: SmeFeatureFlags;
  layoutViews: LayoutView[];
  /** Active failure-injection scenario. */
  scenario: MockScenario;
  /** Simulated network offline switch, independent of the scenario. */
  offline: boolean;
};

function freshDatabase(): MockDatabase {
  return {
    tenant: TENANT,
    programme: PROGRAMME,
    companies: buildCompanies(),
    users: buildUsers(),
    accounts: buildAccounts(),
    ibans: buildIbans(),
    transactions: buildTransactions(),
    beneficiaries: buildBeneficiaries(),
    payments: buildPayments(),
    paymentAttempts: buildPaymentAttempts(),
    approvals: buildApprovals(),
    cards: buildCards(),
    notifications: buildNotifications(),
    featureFlags: buildFeatureFlags(),
    layoutViews: [],
    scenario: 'normal',
    offline: false,
  };
}

let database: MockDatabase = freshDatabase();

export function db(): MockDatabase {
  return database;
}

/** Deterministic full reset. Also clears audit, idempotency, clock and drafts. */
export function resetDatabase(): void {
  database = freshDatabase();
  resetAudit();
  // Seed prior history so the Activity feed shows a populated state on a fresh
  // load rather than only ever an empty one. Without this the empty state was
  // the only reachable state, and the populated case was untestable.
  seedAudit(buildAuditHistory());
  resetIdempotency();
  resetClock();
  clearAllDrafts();
}

export function setScenario(scenario: MockScenario): void {
  database.scenario = scenario;
}

export function getScenario(): MockScenario {
  return database.scenario;
}

export function setOffline(offline: boolean): void {
  database.offline = offline;
}

/** Demo control: toggle a single feature flag to prove feature-disabled states. */
export function setFeatureFlag<K extends keyof SmeFeatureFlags>(
  key: K,
  value: SmeFeatureFlags[K],
): void {
  database.featureFlags = { ...database.featureFlags, [key]: value };
}

/** Demo control: grant or revoke one permission mid-session. */
export function setUserPermission(userId: string, permission: string, granted: boolean): void {
  const user = database.users.find((u) => u.id === userId);
  if (!user) return;
  const held = new Set(user.permissions);
  if (granted) held.add(permission);
  else held.delete(permission);
  user.permissions = [...held];
}

/** Demo control: change a company status to exercise restricted/closed states. */
export function setCompanyStatus(
  companyId: string,
  status: SmeCompanyView['status'],
  reason?: string,
): void {
  const company = database.companies.find((c) => c.id === companyId);
  if (!company) return;
  company.status = status;
  if (reason !== undefined) company.statusReason = reason;
}
